import { useMemo } from 'react';
import {
  HandlersOrganizationResponse,
  useDeleteApiOrganizationsByIdMutation,
  useGetApiOrganizationsQuery,
  useGetApiOrganizationsTypesQuery,
  usePatchApiOrganizationsByIdMutation,
  usePostApiOrganizationsMutation,
} from '../../../../app/athenaApiGen';
import AdminCatalogPanel, { AdminCatalogSelectOption } from '../AdminCatalogPanel';

// Organization master catalog (Pattern A). Each row carries an
// organizationType FK (string PK, not UUID). Required on create and
// mutable on update — server-side deletion of an OrganizationType is
// blocked while any Organization still references it (handled by
// AdminOrganizationTypesPanel's fkConflictMessage).
const AdminOrganizationsPanel = () => {
  // OrganizationType options. Returns straight array (no envelope).
  const { data: orgTypes } = useGetApiOrganizationsTypesQuery();
  const typeOptions = useMemo<AdminCatalogSelectOption[]>(
    () =>
      (orgTypes ?? [])
        .filter((t) => !t.archived)
        .map((t) => ({
          value: t.organizationType ?? '',
          label: t.description
            ? `${t.organizationType} — ${t.description}`
            : (t.organizationType ?? ''),
        })),
    [orgTypes],
  );

  return (
    <AdminCatalogPanel<HandlersOrganizationResponse>
      tableName="Organization"
      apiPath="/api/organizations"
      pkAccessor="organizationId"
      useList={() => useGetApiOrganizationsQuery({})}
      useCreate={usePostApiOrganizationsMutation}
      useUpdate={usePatchApiOrganizationsByIdMutation}
      useDelete={useDeleteApiOrganizationsByIdMutation}
      buildCreateArg={(v) => ({
        handlersCreateOrganizationRequest: {
          name: (v.name as string) ?? '',
          organizationType: (v.organizationType as string) ?? '',
          description: (v.description as string) || undefined,
        },
      })}
      buildUpdateArg={(row, v) => ({
        id: row.organizationId as string,
        handlersUpdateOrganizationRequest: {
          name: (v.name as string) || undefined,
          organizationType: (v.organizationType as string) || undefined,
          description: (v.description as string) || undefined,
        },
      })}
      buildDeleteArg={(row) => ({ id: row.organizationId as string })}
      fields={[
        { name: 'name', label: 'Name', type: 'text', required: true },
        {
          name: 'organizationType',
          label: 'Type',
          type: 'select',
          required: true,
          options: typeOptions,
          helperText: 'Pick from the OrganizationType catalog.',
        },
        {
          name: 'description',
          label: 'Description',
          type: 'textarea',
        },
      ]}
      fkConflictMessage="Other records (Contacts, ProgramObjectType assignments, etc.) still reference this Organization. Reassign those references before deleting."
    />
  );
};

export default AdminOrganizationsPanel;
